
"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ImSearch,
  ImCross,
  ImTruck,
  ImLocation2,
  ImCalendar,
  ImClock,
} from "react-icons/im";
import {
  FaBoxOpen,
  FaWarehouse,
  FaShippingFast,
  FaHome,
  FaPlane,
  FaTrain,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { IoLocationSharp, IoTime, IoCalendar } from "react-icons/io5";
import { MdLocalShipping, MdOutlineDeliveryDining } from "react-icons/md";
import toTitleCase from "@/components/TotitleCase";
import Image from "next/image";
import ServerAddress from "@/constent/Serveraddress";

const TrackingOrder = () => {
  const router = useRouter();
  // const searchParams = useSearchParams();
  const user = "";
  const [orderId, setOrderId] = useState("");
  const [search, setSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState([]);
  const [podDetails, setPodDetails] = useState([]);
  const [openPod, setOpenPod] = useState(null);
  const [docket, setDocket] = useState(null);

  const docketNo = '';

  // Status configuration with icons and colors for Local Order
  const statusConfigLocal = {
    "SHIPMENT ORDER RECEIVED": {
      icon: FaBoxOpen,
      color: "bg-blue-500",
      progress: 0,
    },
    "SHIPMENT PICKED UP": {
      icon: MdLocalShipping,
      color: "bg-purple-500",
      progress: 25,
    },
    "SHIPMENT ARRIVED AT HUB": {
      icon: FaWarehouse,
      color: "bg-indigo-500",
      progress: 50,
    },
    "SHIPMENT OUT FOR DELIVERY": {
      icon: MdOutlineDeliveryDining,
      color: "bg-red-500",
      progress: 75,
    },
    "SHIPMENT DELIVERED": {
      icon: FaCheckCircle,
      color: "bg-green-500",
      progress: 100,
    },
    "SHORT DELIVERED": {
      icon: FaExclamationTriangle,
      color: "bg-red-600",
      progress: 100,
    },
  };

  // Status configuration with icons and colors for Domestic Order
  const statusConfigDomestic = {
    "SHIPMENT ORDER RECEIVED": {
      icon: FaBoxOpen,
      color: "bg-blue-500",
      progress: 0,
    },
    "SHIPMENT PICKED UP": {
      icon: MdLocalShipping,
      color: "bg-purple-500",
      progress: 17,
    },
    "SHIPMENT ARRIVED AT HUB": {
      icon: FaWarehouse,
      color: "bg-indigo-500",
      progress: 33,
    },
    "SHIPMENT IN TRANSIT": {
      icon: FaShippingFast,
      color: "bg-yellow-500",
      progress: 50,
    },
    "SHIPMENT ARRIVED AT DESTINATION": {
      icon: FaHome,
      color: "bg-orange-500",
      progress: 67,
    },
    "SHIPMENT OUT FOR DELIVERY": {
      icon: MdOutlineDeliveryDining,
      color: "bg-red-500",
      progress: 83,
    },
    "SHIPMENT DELIVERED": {
      icon: FaCheckCircle,
      color: "bg-green-500",
      progress: 100,
    },
    "SHORT DELIVERED": {
      icon: FaExclamationTriangle,
      color: "bg-red-600",
      progress: 100,
    },
  };

  const getOrderData = async (docket) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `/tracking-api/booking/get_order_status/?awb_no=${docket}&user_type=${user?.user_type ?? null}&filter_data=${user?.user_type === "BILL TO" ? user?.billto || null : user?.client || null}`,
      );

      setOpenPod(null);
      setDocket(null);

      if (response.data?.length > 0 && response.data[0]?.length > 0) {
        const docketInfo = response.data[0];
        const last = docketInfo[docketInfo.length - 1];
        setStatus(last);
        setOrders(response.data.map((item) => [...item, false]));
        setSearch(true);
      } else {
        setError("Docket Not Found");
      }
    } catch (err) {
      setError(`Error fetching order data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPodDetails = async (docket) => {
    try {
      const response = await axios.get(
        `/tracking-api/booking/get_track_delivery/?docket=${docket}`,
      );
      setPodDetails(response.data?.length > 0 ? response.data : []);
    } catch (err) {
      setError(`Error fetching POD details: ${err.message}`);
    }
  };

  useEffect(() => {
    if (docketNo) {
      setOrderId(docketNo);
      getOrderData(docketNo);
    }
  }, [docketNo]);

  useEffect(() => {
    if (docket) {
      getPodDetails(docket);
    }
  }, [docket]);

  const formatDate = (bookingAt, etaDays) => {
    if (!etaDays || !bookingAt)
      return { formattedBookingDate: "", formattedDate: "" };

    const orderBookingDate = new Date(bookingAt);
    const estimatedDate = new Date(bookingAt);
    estimatedDate.setDate(estimatedDate.getDate() + parseInt(etaDays));

    return {
      formattedDate: estimatedDate.toLocaleDateString("en-GB"),
      formattedBookingDate: orderBookingDate.toLocaleDateString("en-GB"),
    };
  };

  const getStatusProgress = (status, isLocal = false) => {
    const config = isLocal ? statusConfigLocal : statusConfigDomestic;
    const cleanStatus = status?.trim()?.toUpperCase() || "";
    const normalizedStatus =
      cleanStatus === "SHIPMENT DELIVERED"
        ? "SHIPMENT DELIVERED"
        : cleanStatus === "SHORT DELIVERED"
          ? "SHIPMENT DELIVERED" // Map SHORT DELIVERED to SHIPMENT DELIVERED for progress
          : cleanStatus === "SHIPMENT ARRIVED AT DESTINATION HUB" || cleanStatus === "SHIPMENT FURTHER CONNECTED"
            ? "SHIPMENT ARRIVED AT DESTINATION"
            : cleanStatus;
    return config[normalizedStatus]?.progress || 0;
  };

  const getTransportIcon = (mode) => {
    switch (mode?.toUpperCase()) {
      case "AIR":
        return FaPlane;
      case "TRAIN":
        return FaTrain;
      default:
        return ImTruck;
    }
  };

  // Fixed StatusStep component - moved outside the render
  const StatusStep = ({ status, isActive, isCompleted, transportMode, isLocal, actualStatus }) => {
    const config = isLocal ? statusConfigLocal : statusConfigDomestic;
    const statusConfig = config[status];
    const IconComponent =
      status === "SHIPMENT IN TRANSIT"
        ? getTransportIcon(transportMode)
        : statusConfig?.icon;

    return (
      <div className="flex flex-col items-center relative">
        {/* Connection line for mobile */}
        <div className="sm:hidden absolute -top-5 left-1/2 transform -translate-x-1/2 w-px h-4 bg-gray-200"></div>

        <div
          className={`w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white z-10 ${
            isCompleted
              ? "bg-green-500"
              : isActive
                ? statusConfig?.color
                : "bg-gray-300"
          }`}
        >
          <IconComponent size={14} className="sm:w-4 sm:h-4" />
        </div>
        <span
          className={`text-xs mt-1 sm:mt-2 text-center px-1 leading-tight ${
            isActive || isCompleted ? "font-semibold" : "text-gray-500"
          }`}
        >
          {/* Mobile abbreviated text */}
          <span className="sm:hidden">
            {status === "SHIPMENT ARRIVED AT DESTINATION" && (actualStatus === "SHIPMENT ARRIVED AT DESTINATION HUB" || actualStatus === "SHIPMENT FURTHER CONNECTED")
              ? "At Dest Hub"
              : status
                  .replace("SHIPMENT ", "")
                  .replace("ORDER RECEIVED", "Received")
                  .replace("PICKED UP", "Picked Up")
                  .replace("ARRIVED AT HUB", "At Hub")
                  .replace("IN TRANSIT", "In Transit")
                  .replace("ARRIVED AT DESTINATION", "At Destination")
                  .replace("OUT FOR DELIVERY", "Out for Delivery")
                  .replace("DELIVERED", "Delivered")}
          </span>
          {/* Desktop full text */}
          <span className="hidden sm:inline">
            {status === "SHIPMENT ARRIVED AT DESTINATION" && (actualStatus === "SHIPMENT ARRIVED AT DESTINATION HUB" || actualStatus === "SHIPMENT FURTHER CONNECTED")
              ? " ARRIVED AT DESTINATION HUB"
              : status.replace("SHIPMENT", "")}
          </span>
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 overflow-y-auto">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="text-center">
            <h1 className="text-xl font-bold text-gray-900 mb-1 flex items-center justify-center">
              Track Your Shipment
              <IoLocationSharp className="ml-3 text-blue-600" />
            </h1>
            <p className="text-sm text-gray-600">
              Enter your docket number to track your package in real-time
            </p>
          </div>
        </div>
      </motion.header>

      {/* Search Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="max-w-2xl mx-auto px-4 py-4"
      >
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (orderId.length > 5) {
                getOrderData(orderId);
              } else {
                setError(
                  "Please enter a valid docket number (min. 6 characters)",
                );
              }
            }}
            className="space-y-4"
          >
            <div className="flex space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter your docket number..."
                  value={orderId}
                  onChange={(e) => {
                    setOrderId(e.target.value);
                    setError("");
                  }}
                  className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white p-4 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ImSearch size={20} />
                )}
              </motion.button>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                {error}
              </motion.div>
            )}
          </form>
        </div>
      </motion.section>

      {/* Results Section */}
      <AnimatePresence>
        {search &&
          orders.map((item, index) => {
            const lastEle = item[item.length - 2];
            console.log("lastEle", lastEle);
            const { formattedBookingDate, formattedDate } = formatDate(
              lastEle?.booking_at,
              lastEle?.eta_days,
            );
            
            const isLocal = lastEle?.delivery_type?.toUpperCase() === "LOCAL";
            const isDomestic = lastEle?.delivery_type?.toUpperCase() === "DOMESTIC";
            const currentConfig = isLocal ? statusConfigLocal : statusConfigDomestic;
            const currentProgress = getStatusProgress(lastEle?.status, isLocal);

            return (
              <motion.section
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                className="max-w-6xl mx-auto px-2 py-3"
              >
                {/* Summary Card */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-bold">Shipment Details</h2>
                        <p className="text-blue-100">
                          Docket: {lastEle?.docket_no} 
                          {isLocal && <span className="ml-2 bg-yellow-500 px-2 py-1 rounded text-xs">LOCAL</span>}
                          {isDomestic && <span className="ml-2 bg-yellow-500 px-2 py-1 rounded text-xs">DOMESTIC</span>}
                        </p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            lastEle?.status === "SHIPMENT DELIVERED"
                              ? "bg-green-500"
                              : lastEle?.status === "SHORT DELIVERED"
                                ? "bg-red-500"
                                : "bg-yellow-500"
                          }`}
                        >
                          {toTitleCase(lastEle?.status)}
                        </span>
                        {lastEle?.issue_notreceived?.length > 0 &&
                          lastEle?.status !== "SHIPMENT DELIVERED" && (
                            <span className="bg-red-500 px-3 py-1 rounded-full text-sm font-semibold shadow-sm text-white">
                              Not Received Packets: {lastEle?.issue_notreceived?.length}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <ImLocation2 className="mr-2 text-blue-600" />
                        <span className="font-semibold">Origin</span>
                      </div>
                      <p className="text-lg">
                        {toTitleCase(lastEle?.origin_city)},{" "}
                        {toTitleCase(lastEle?.origin_state)}
                      </p>
                      <p className="text-gray-500">{lastEle?.origin_pincode}</p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <ImLocation2 className="mr-2 text-green-600" />
                        <span className="font-semibold">Destination</span>
                      </div>
                      <p className="text-lg">
                        {toTitleCase(lastEle?.destination_city)},{" "}
                        {toTitleCase(lastEle?.destination_state)}
                      </p>
                      <p className="text-gray-500">
                        {lastEle?.destination_pincode}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <ImCalendar className="mr-2 text-purple-600" />
                        <span className="font-semibold">Timeline</span>
                      </div>
                      <p>Booked: {formattedBookingDate}</p>
                      <p>ETA: {formattedDate}</p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <ImClock className="mr-2 text-orange-600" />
                        <span className="font-semibold">Current Location</span>
                      </div>
                      <p className="text-lg">
                        {toTitleCase(lastEle?.current_city)},{" "}
                        {toTitleCase(lastEle?.current_state)}
                      </p>
                      <p className="text-gray-500">
                        {lastEle?.current_pincode}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Progress Tracker - Fixed responsive version */}
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center">
                    <IoTime className="mr-2 text-blue-600" />
                    Shipping Progress {isLocal && <span className="ml-2 text-sm text-gray-600">(Local Delivery)</span>}
                  </h3>

                  <div className="relative">
                    {/* Progress Bar - Horizontal for desktop */}
                    <div className="hidden sm:block absolute top-6 left-0 right-0 h-2 bg-gray-200 rounded-full">
                      <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${currentProgress}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>

                    {/* Progress Bar - Vertical for mobile */}
                    <div className="sm:hidden absolute left-4 top-0 bottom-0 w-1 bg-gray-200 rounded-full">
                      <motion.div
                        className="w-full bg-gradient-to-b from-blue-500 to-green-500 rounded-full"
                        initial={{ height: 0 }}
                        animate={{ height: `${currentProgress}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>

                    {/* Status Steps - Responsive layout */}
                    <div className="relative grid grid-cols-1 sm:flex sm:justify-between gap-4 sm:gap-0 ml-8 sm:ml-0">
                      {Object.keys(currentConfig)
                        .filter(statusKey => statusKey !== "SHORT DELIVERED") // Filter out SHORT DELIVERED
                        .map((statusKey, idx) => {
                          const currentStatus = lastEle?.status?.trim()?.toUpperCase() || "";
                          const isCompleted =
                            currentProgress > currentConfig[statusKey].progress;
                          const isActive =
                            currentStatus === statusKey ||
                            (statusKey === "SHIPMENT DELIVERED" &&
                              (currentStatus === "SHIPMENT DELIVERED" ||
                                currentStatus === "SHORT DELIVERED")) ||
                            (statusKey === "SHIPMENT ARRIVED AT DESTINATION" &&
                              (currentStatus === "SHIPMENT ARRIVED AT DESTINATION HUB" ||
                                currentStatus === "SHIPMENT FURTHER CONNECTED"));

                          return (
                            <div
                              key={idx}
                              className="flex items-center sm:block sm:text-center gap-3 sm:gap-0"
                            >
                              {/* Mobile dot indicator */}
                              <div className="sm:hidden absolute -left-4 w-3 h-3 rounded-full bg-gray-300 z-10"></div>
                              
                              <StatusStep
                                status={statusKey}
                                isActive={isActive}
                                isCompleted={isCompleted}
                                transportMode={lastEle?.delivery_type}
                                isLocal={isLocal}
                                actualStatus={currentStatus}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 justify-end mb-6">
                  {(lastEle?.status === "SHIPMENT DELIVERED" || lastEle?.status === "SHORT DELIVERED") && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setOpenPod(openPod === index ? null : index);
                        setDocket(lastEle?.docket);
                      }}
                      className="bg-green-600 text-white px-6 py-3 rounded-xl flex items-center font-semibold"
                    >
                      <FaCheckCircle className="mr-2" />
                      View Proof of Delivery
                    </motion.button>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const newOrders = [...orders];
                      newOrders[index][newOrders[index].length - 1] =
                        !newOrders[index][newOrders[index].length - 1];
                      setOrders(newOrders);
                    }}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl flex items-center font-semibold"
                  >
                    {item[item.length - 1]
                      ? "Hide Details"
                      : "View Full History"}
                  </motion.button>
                </div>

                {/* POD Gallery */}
                <AnimatePresence>
                  {openPod === index && podDetails.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-white rounded-2xl shadow-lg p-6 mb-6"
                    >
                      <h3 className="text-xl font-bold mb-4 flex items-center">
                        <FaCheckCircle className="mr-2 text-green-600" />
                        Proof of Delivery
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {podDetails[0]?.delivery_image?.map((value, idx) => (
                          <motion.div
                            key={idx}
                            whileHover={{ scale: 1.02 }}
                            className="cursor-pointer border rounded-lg overflow-hidden"
                            onClick={() => window.open(value.image, "_blank")}
                          >
                            <img
                              src={value.image}
                              alt={`POD ${idx + 1}`}
                              className="w-full h-48 object-cover"
                              // width={200}
                              // height={200}
                            />
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Tracking History */}
                <AnimatePresence>
                  {item[item.length - 1] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-white rounded-2xl shadow-lg overflow-hidden"
                    >
                      <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                        <h3 className="text-xl font-bold flex items-center">
                          <ImClock className="mr-2 text-blue-600" />
                          Tracking History
                        </h3>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          onClick={() => {
                            const newOrders = [...orders];
                            newOrders[index][newOrders[index].length - 1] =
                              false;
                            setOrders(newOrders);
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <ImCross size={16} />
                        </motion.button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status & Details
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {item.slice(0, -1).map((item1, idx) => {
                              const [date, timeWithOffset] =
                                item1?.created_at?.split("T") || [];
                              const [time] = timeWithOffset?.split(".") || [];
                              const dUpdateDate = date
                                ? new Date(date).toLocaleDateString("en-GB")
                                : "-";
                              const updateTime = time
                                ? time.substring(0, 8)
                                : "-";

                              return (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="font-medium text-gray-900">
                                      {toTitleCase(item1.status)}
                                    </div>
                                    {item1.transit_status && (
                                      <div className="text-sm text-gray-500">
                                        {toTitleCase(item1.transit_status)}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <IoCalendar className="mr-2 text-gray-400" />
                                      {dUpdateDate}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <ImClock className="mr-2 text-gray-400" />
                                      {updateTime}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    {lastEle?.delivery_type === "INTERNATIONAL"
                                      ? item1?.international_location
                                        ? toTitleCase(
                                            item1.international_location,
                                          )
                                        : "-"
                                      : `${toTitleCase(item1.status_current_city)}, ${toTitleCase(item1.status_current_state)}`}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.section>
            );
          })}
      </AnimatePresence>
    </div>
  );
};

export default TrackingOrder;